/**
 * =============================================================================
 * Componente: ContractPdfPagePreview
 * Versão: 1.0.0 (2026-05-01)
 *
 * Descrição:
 *   Renderiza um bloco A4 escalado contendo um PDF (capa ou PDF base) via
 *   <iframe> nativo. Usado por ContractFullPreview para compor a visualização
 *   completa: capa + HTML do anexo + PDF base.
 *
 *   - Usa as mesmas constantes A4 do preview de HTML (paridade visual).
 *   - Aceita `scale` externo para sincronizar com os outros blocos.
 *   - Fallback acessível com link "Abrir PDF em nova aba" caso o navegador
 *     não suporte iframes de PDF (alguns mobiles).
 * =============================================================================
 */
import { ExternalLink } from 'lucide-react';
import { A4_WIDTH_PX, A4_HEIGHT_PX } from './contractStyles';

interface Props {
  /** URL utilizável do PDF (já resolvida para http(s)) */
  url: string;
  /** Rótulo do bloco (acessibilidade + badge superior) */
  label: string;
  /** Fator de escala visual */
  scale: number;
  /**
   * Quantas "páginas A4 escaladas" reservar verticalmente. Como o iframe
   * de um PDF de N páginas exibe scroll interno, mantemos 1 página A4 como
   * altura visível padrão (suficiente para capa). Para PDF base com várias
   * páginas, o usuário pode rolar dentro do iframe ou abrir em nova aba.
   */
  visualPages?: number;
}

export function ContractPdfPagePreview({ url, label, scale, visualPages = 1 }: Props) {
  // Dimensões visuais escaladas
  const scaledWidth = A4_WIDTH_PX * scale;
  const scaledHeight = A4_HEIGHT_PX * scale * visualPages;

  // Dimensões reais do iframe (antes da escala)
  const realHeight = A4_HEIGHT_PX * visualPages;

  // Hash de viewer: oculta UI nativa, ajusta zoom para largura da página
  const src = `${url}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`;

  return (
    <div
      style={{
        width: `${scaledWidth}px`,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Badge de identificação do bloco */}
      <div
        style={{
          fontSize: '11px',
          color: '#666',
          marginBottom: '4px',
          fontFamily: 'system-ui, sans-serif',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>

      {/* Wrapper escalado */}
      <div
        style={{
          width: `${scaledWidth}px`,
          height: `${scaledHeight}px`,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          backgroundColor: '#ffffff',
        }}
      >
        <iframe
          title={label}
          src={src}
          loading="lazy"
          style={{
            width: `${A4_WIDTH_PX}px`,
            height: `${realHeight}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            border: 0,
            backgroundColor: '#ffffff',
          }}
        />
      </div>

      {/* Fallback / abertura em nova aba (também útil em mobiles que não
          renderizam PDFs em iframe inline) */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
      >
        <ExternalLink className="h-3 w-3" />
        Abrir {label.toLowerCase()} em nova aba
      </a>
    </div>
  );
}
